import { Moment } from 'moment';

export class Campaign {
  id?: string;
  campaignId?: string;
  name?: string;
  isDraft?: boolean;
  status?: string;
  createdDate?: Date;
  updatedDate?: Date;
  createdUser?: string;
  updatedUser?: string;
  recipientCampaignId?: string;
  recipientCampaignStatus?: string;
  nextStepScheduledDate?: Date;
  focus?: string;
  locale?: string;
  campaignSchedule?: CampaignSchedule[];

  public static createFromRawData(rawData: any): Campaign {
    const campaign = new Campaign();
    campaign.id = rawData.campaign_id;
    campaign.name = rawData.name;
    campaign.status = rawData.status;
    campaign.locale = rawData.locale;
    campaign.isDraft = rawData.is_draft;
    campaign.focus = rawData.campaign_focus;
    campaign.createdDate = new Date(rawData.created);
    campaign.updatedDate = new Date(rawData.updated);
    campaign.createdUser = rawData.created_user;
    campaign.updatedUser = rawData.updated_user;
    campaign.campaignSchedule = [];
    if (rawData.campaign_schedule) {
      campaign.campaignSchedule = rawData.campaign_schedule.map((scheduleData) =>
        this.createScheduleData(scheduleData),
      );
    }
    return campaign;
  }

  private static createScheduleData(campaignSchedule: any): CampaignSchedule {
    return {
      campaignStepId: campaignSchedule.campaign_step_id,
      name: campaignSchedule.name,
      secondsAfterLastEmail: campaignSchedule.seconds_after_last_email,
      stepType: campaignSchedule.step_type,
      templateId: campaignSchedule.template_id,
    };
  }

  get currentStatus(): string {
    if (this.status === 'draft' || this.status === 'archived') {
      return null;
    } else if (this.recipientCampaignStatus === 'active') {
      return 'active';
    } else if (this.recipientCampaignStatus === 'stopped') {
      return 'paused';
    } else if (this.recipientCampaignStatus === 'waiting_on_rate_limit') {
      return 'buffering';
    } else if (this.recipientCampaignStatus === 'completed') {
      return 'completed';
    } else {
      console.error('unexpected statuses', this.status, this.recipientCampaignStatus);
      return null;
    }
  }

  get started(): boolean {
    return !!this.recipientCampaignId;
  }
}

export class AutomationConstants {
  public static START_CAMPAIGN = 'start-campaign';
  public static STOP_CAMPAIGN = 'stop-campaign';
  public static PRODUCT_TRIGGER = 'product-activation';
}

export interface CampaignEvent {
  recipientName: string;
  recipientEmail: string;
  accountGroupId: string;
  accountName: string;
  accountAddress: string;
  eventStep: string;
  activity: string;
  time: Moment;
}

export interface CampaignStepTitle {
  campaignStepId: string;
  title: string;
}

export interface RecipientCampaignStatusResponse {
  recipientCampaignId: string;
  scheduledTime: Date;
  stageName: string;
  stageType: string;
  status: string;
}

export interface SimpleCampaign {
  id: string;
  name: string;
  status: string;
}

export interface LookupCampaignsResponse {
  campaigns: SimpleCampaign[];
  offset?: string;
}

export interface RecipientCampaign {
  recipientId: string;
  recipientCampaignId: string;
  status: string;
  nextStepScheduledDate: string;
}

export interface RecipientCampaignsResponse {
  [campaignId: string]: RecipientCampaign;
}

export interface CampaignSchedule {
  campaignStepId: string;
  stepType: string;
  name: string;
  secondsAfterLastEmail: number;
  templateId: string;
}

export interface CampaignDelay {
  delay: number;
  validDays: string[];
  timezone: string;
  rateLimitEnabled: boolean;
  rateLimit: number;
}

export interface CampaignAutomations {
  automationID: string;
  productId: string;
  triggerId?: string;
  actionID?: string;
}

export interface AutomationIds {
  start_automation_id: string;
  stop_automation_id: string;
}
